import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles, alpha } from "@material-ui/core/styles";
import { grey, blue } from "@material-ui/core/colors";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

// Icons
import AndroidIcon from "@material-ui/icons/Android";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloseIcon from "@material-ui/icons/Close";
import ReplayIcon from "@material-ui/icons/Replay";
import CheckIcon from "@material-ui/icons/Check";

import TicketMessagesDialog from "../TicketMessagesDialog";
import ContactTag from "../ContactTag";

const useStyles = makeStyles((theme) => ({
  // Card do ticket
  ticketCard: {
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(0.5),
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" 
      ? alpha("#ffffff", 0.03)
      : "#ffffff",
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    transition: "all 0.2s ease",
    cursor: "pointer",
    overflow: "hidden",
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(0.75),
      borderRadius: 14,
    },
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" 
        ? alpha("#ffffff", 0.06)
        : alpha("#6366F1", 0.04),
      borderColor: alpha(theme.palette.primary.main, 0.2),
      transform: "translateX(2px)",
      [theme.breakpoints.up("md")]: {
        transform: "translateX(4px)",
      },
    },
  },

  ticketCardSelected: {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    borderColor: theme.palette.primary.main,
    borderLeftWidth: 3,
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  },

  ticketCardPending: {
    borderLeftWidth: 3,
    borderLeftColor: "#F59E0B",
    backgroundColor: theme.palette.type === "dark" 
      ? alpha("#F59E0B", 0.05)
      : alpha("#F59E0B", 0.03),
  },

  // Queue indicator
  queueIndicator: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    borderRadius: "3px 0 0 3px",
    [theme.breakpoints.up("md")]: {
      width: 4,
      borderRadius: "4px 0 0 4px",
    },
  },

  // Avatar
  avatarWrapper: {
    position: "relative",
    marginRight: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.up("md")]: {
      marginRight: theme.spacing(1.5),
    },
  },

  avatar: {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
    [theme.breakpoints.up("md")]: {
      width: 52,
      height: 52,
      borderRadius: 14,
    },
  },

  unreadBadge: {
    position: "absolute",
    top: -3,
    right: -3,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: "#22C55E",
    color: "#fff",
    fontSize: "0.6rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: "0 2px 8px rgba(34, 197, 94, 0.4)",
    [theme.breakpoints.up("md")]: {
      top: -4,
      right: -4,
      minWidth: 22,
      height: 22,
      borderRadius: 11,
      fontSize: "0.7rem",
    },
  },

  // Content
  contentWrapper: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.25),
    [theme.breakpoints.up("md")]: {
      gap: theme.spacing(0.5),
    },
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(0.5),
    [theme.breakpoints.up("md")]: {
      gap: theme.spacing(1),
    },
  },

  contactName: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    [theme.breakpoints.up("md")]: {
      fontSize: "0.95rem",
    },
  },

  timestamp: {
    fontSize: "0.65rem",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    [theme.breakpoints.up("md")]: {
      fontSize: "0.7rem",
    },
  },

  lastMessage: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: 1.4,
    "& p": {
      margin: 0,
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "0.82rem",
    },
  },

  // Tags row
  tagsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.4),
    flexWrap: "wrap",
    marginTop: theme.spacing(0.25),
    [theme.breakpoints.up("md")]: {
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
    },
  },

  chip: {
    height: 18,
    fontSize: "0.6rem",
    fontWeight: 600,
    borderRadius: 5,
    "& .MuiChip-label": {
      padding: "0 5px",
    },
    [theme.breakpoints.up("md")]: {
      height: 20,
      fontSize: "0.65rem",
      borderRadius: 6,
      "& .MuiChip-label": {
        padding: "0 6px",
      },
    },
  },

  queueChip: {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },

  connectionChip: {
    backgroundColor: alpha("#22C55E", 0.1),
    color: "#16A34A",
  },

  userChip: {
    backgroundColor: alpha("#8B5CF6", 0.1),
    color: "#7C3AED",
  },

  // Icons
  chatbotIcon: {
    fontSize: 14,
    color: grey[500],
    [theme.breakpoints.up("md")]: {
      fontSize: 16,
    },
  },

  peekIcon: {
    fontSize: 14,
    color: blue[500],
    cursor: "pointer",
    "&:hover": {
      color: blue[700],
    },
    [theme.breakpoints.up("md")]: {
      fontSize: 16,
    },
  },

  // Actions
  actionsWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.4),
    marginLeft: theme.spacing(0.5),
    flexShrink: 0,
    [theme.breakpoints.up("md")]: {
      gap: theme.spacing(0.5),
      marginLeft: theme.spacing(1),
    },
  },

  actionButton: {
    minWidth: 28,
    width: 28,
    height: 28,
    borderRadius: 8,
    padding: 0,
    "& .MuiButton-startIcon": {
      margin: 0,
    },
    "& svg": {
      fontSize: 14,
    },
    [theme.breakpoints.up("md")]: {
      minWidth: 32,
      width: 32,
      height: 32,
      borderRadius: 10,
      "& svg": {
        fontSize: 16,
      },
    },
  },

  acceptButton: {
    backgroundColor: "#22C55E",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#16A34A",
    },
  },

  closeButton: {
    backgroundColor: alpha("#EF4444", 0.1),
    color: "#EF4444",
    "&:hover": {
      backgroundColor: "#EF4444",
      color: "#fff",
    },
  },

  reopenButton: {
    backgroundColor: alpha("#6366F1", 0.1),
    color: "#6366F1",
    "&:hover": {
      backgroundColor: "#6366F1",
      color: "#fff",
    },
  },
}));

const TicketListItemCustom = ({ ticket }) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [ticketUser, setTicketUser] = useState(null);
  const [tag, setTag] = useState([]);

  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  const { profile } = user;

  useEffect(() => {
    if (ticket.userId && ticket.user) {
      setTicketUser(ticket.user?.name);
    }
    setTag(ticket?.tags);

    return () => {
      isMounted.current = false;
    };
  }, [ticket]);

  const handleCloseTicket = async (id) => {
    setTag(ticket?.tags);
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id,
        queueId: ticket?.queue?.id,
        useIntegration: false,
        promptId: null,
        integrationId: null
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }
    history.push(`/tickets/`);
  };

  const handleReopenTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
        queueId: ticket?.queue?.id
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
      });

      let settingIndex;

      try {
        const { data } = await api.get("/settings/");
        settingIndex = data.filter((s) => s.key === "sendGreetingAccepted");
      } catch (err) {
        toastError(err);
      }

      if (settingIndex[0].value === "enabled" && !ticket.isGroup) {
        handleSendMessage(ticket.id);
      }

    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleSendMessage = async (id) => {
    const msg = `{{ms}} *{{name}}*, meu nome é *${user?.name}* e agora vou prosseguir com seu atendimento!`;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `*Mensagem Automática:*\n${msg.trim()}`,
    };
    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleCardClick = (e) => {
    if (ticket.status === "pending") return;
    handleSelectTicket(ticket);
  };

  const isSelected = ticketId && +ticketId === ticket.id;

  return (
    <React.Fragment key={ticket.id}>
      <TicketMessagesDialog
        open={openTicketMessageDialog}
        handleClose={() => setOpenTicketMessageDialog(false)}
        ticketId={ticket.id}
      />

      <Box
        className={clsx(classes.ticketCard, {
          [classes.ticketCardSelected]: isSelected,
          [classes.ticketCardPending]: ticket.status === "pending",
        })}
        onClick={handleCardClick}
      >
        {/* Queue Indicator */}
        <Box
          className={classes.queueIndicator}
          style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
        />

        {/* Avatar */}
        <Box className={classes.avatarWrapper}>
          <Avatar
            className={classes.avatar}
            src={ticket?.contact?.profilePicUrl}
          />
          {ticket.unreadMessages > 0 && (
            <Box className={classes.unreadBadge}>
              {ticket.unreadMessages > 9 ? "9+" : ticket.unreadMessages}
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box className={classes.contentWrapper}>
          {/* Header */}
          <Box className={classes.headerRow}>
            <Typography className={classes.contactName}>
              {ticket.contact.name}
            </Typography>
            <Typography className={classes.timestamp}>
              {ticket.lastMessage && (
                isSameDay(parseISO(ticket.updatedAt), new Date())
                  ? format(parseISO(ticket.updatedAt), "HH:mm")
                  : format(parseISO(ticket.updatedAt), "dd/MM")
              )}
            </Typography>
          </Box>

          {/* Last Message */}
          <Typography className={classes.lastMessage}>
            {ticket.lastMessage.includes('data:image/png;base64') 
              ? "📍 Localização" 
              : <MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
            }
          </Typography>

          {/* Tags */}
          <Box className={classes.tagsRow}>
            {ticket.queue && (
              <Chip
                label={ticket.queue.name}
                size="small"
                className={classes.chip}
                style={{
                  backgroundColor: alpha(ticket.queue.color || "#6366F1", 0.15),
                  color: ticket.queue.color || "#6366F1",
                }}
              />
            )}
            {ticket?.whatsapp?.name && (
              <Chip
                label={ticket.whatsapp.name}
                size="small"
                className={`${classes.chip} ${classes.connectionChip}`}
              />
            )}
            {ticketUser && (
              <Chip
                label={ticketUser.split(" ")[0]}
                size="small"
                className={`${classes.chip} ${classes.userChip}`}
              />
            )}
            {ticket.chatbot && (
              <Tooltip title={i18n.t("ticketsListItem.tooltip.chatbot")}>
                <AndroidIcon className={classes.chatbotIcon} />
              </Tooltip>
            )}
            {profile === "admin" && (
              <Tooltip title={i18n.t("ticketsListItem.tooltip.peek")}>
                <VisibilityIcon
                  className={classes.peekIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenTicketMessageDialog(true);
                  }}
                />
              </Tooltip>
            )}
          </Box>

          {/* Contact Tags */}
          {tag && tag.length > 0 && (
            <Box className={classes.tagsRow}>
              {tag.map((t) => (
                <ContactTag tag={t} key={`ticket-contact-tag-${ticket.id}-${t.id}`} />
              ))}
            </Box>
          )}
        </Box>

        {/* Actions */}
        <Box className={classes.actionsWrapper}>
          {ticket.status === "pending" && (
            <Tooltip title={i18n.t("ticketsList.buttons.accept")}>
              <ButtonWithSpinner
                className={`${classes.actionButton} ${classes.acceptButton}`}
                variant="contained"
                size="small"
                loading={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAcepptTicket(ticket.id);
                }}
              >
                <CheckIcon />
              </ButtonWithSpinner>
            </Tooltip>
          )}

          {ticket.status !== "closed" && (
            <Tooltip title={i18n.t("ticketsList.buttons.closed")}>
              <ButtonWithSpinner
                className={`${classes.actionButton} ${classes.closeButton}`}
                variant="contained"
                size="small"
                loading={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseTicket(ticket.id);
                }}
              >
                <CloseIcon />
              </ButtonWithSpinner>
            </Tooltip>
          )}

          {ticket.status === "closed" && (
            <Tooltip title={i18n.t("ticketsList.buttons.reopen")}>
              <ButtonWithSpinner
                className={`${classes.actionButton} ${classes.reopenButton}`}
                variant="contained"
                size="small"
                loading={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReopenTicket(ticket.id);
                }}
              >
                <ReplayIcon />
              </ButtonWithSpinner>
            </Tooltip>
          )}
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default TicketListItemCustom;
